<template>
  <div class="audittrail">
    <v-container>
      <h2 class="mt-5 mb-10">{{$t('audit_trail')}}</h2>
      <v-card class="px-6 pb-0 mb-3 mt-3">
        <v-row>
          <v-col md="3" class="pb-0">
            <v-menu
              ref="menu"
              v-model="menu_start"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="dateFormatted(start_date)"
                  :label="$t('start_date')"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-on="on"
                  color="primary"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="start_date"
                @change="onStartDateChange"
                :max="todayDate"
                no-title
                scrollable
                :locale="$t('locale')"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col md="3" class="pb-0">
            <v-menu
              ref="menu"
              v-model="menu_end"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="dateFormatted(end_date)"
                  :label="$t('end_date')"
                  prepend-icon="mdi-calendar"
                  readonly
                  v-on="on"
                  color="primary"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="end_date"
                @change="onEndDateChange"
                :min="start_date"
                :max="todayDate"
                no-title
                scrollable
                :locale="$t('locale')"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col md="3">
            <v-autocomplete
              :label="$t('find_a_user')"
              :items="userList"
              v-model="id"
              item-text="full_name"
              item-value="id"
            ></v-autocomplete>
          </v-col>
          <v-col md="3">
            <v-autocomplete
              :label="$t('find_by_action')"
              :items="actionList"
              v-model="actionId"
              item-text="name"
              item-value="id"
            ></v-autocomplete>
          </v-col>
          <v-col md="3">
            <v-autocomplete
              :label="$t('find_by_menu')"
              :items="menuList"
              v-model="menuId"
              item-text="name"
              item-value="key"
            ></v-autocomplete>
          </v-col>
          <v-col md="3"></v-col>
          <v-col md="3" class="pa-6">
            <v-btn block color="primary" @click="onSelectUser">
              <v-icon>mdi-magnify</v-icon>
              {{$t('search')}}
            </v-btn>
          </v-col>
          <v-col md="3" class="pa-6">
            <v-btn block color="primary" @click="getDownload">
              <v-icon>mdi-download</v-icon>
              {{$t('download')}}
            </v-btn>
          </v-col>
        </v-row>
      </v-card>
    </v-container>

    <v-container>
      <v-card>
        <v-data-table
          v-if="!firstLoad"
          :loading="loading"
          :headers="headers"
          :items="items"
          class="elevation-1"
          :disable-sort="disableSort"
          :server-items-length="totalData"
          :items-per-page.sync="itemPerPage"
          :page="page"
          @update:page="fetchAudit($event, itemPerPage)"
          @update:items-per-page="fetchAudit(page, $event)"
          :no-data-text="$t('no_data')"
          :footer-props="{ 'items-per-page-options': [10, 50, 100], 'items-per-page-text': $t('rows_per_page') }"
        >
          <template v-slot:item.user.full_name="{ item }">{{item.user.full_name}}</template>
          <template
            v-slot:item.transactionDate="{ item }"
          >{{dateFormatted(item.transactionDate)}} {{dateFormattedHour(item.transactionDate)}}</template>

          <template v-slot:item.detail="{ item }">{{parsing(item.detail)}}</template>

          <template v-slot:item.menu="{ item }">{{$t(item.menu.toLowerCase())}}</template>

          <template
            v-slot:item.userEvent="{ item }"
          >{{capitalizeNoTranslate($t(item.userEvent.toLowerCase()))}}</template>

          <template v-slot:item.user.role="{ item }">
            <div v-if="item.user.role == 0">{{$t('admin_hrd')}}</div>
            <div v-if="item.user.role == 1">{{$t('staff')}}</div>
            <div v-if="item.user.role == 2">{{$t('admin_appsensi')}}</div>
            <div v-if="item.user.role == 3">{{$t('admin_group')}}</div>
            <div v-if="item.user.role == 4">{{$t('executive')}}</div>
          </template>

          <template v-slot:item.action="{ item }">
            <v-row align="center">
              <v-col cols="12" class="text-center">
                <v-btn
                  color="primary accent-3"
                  icon
                  small
                  @click="openDetail(item)"
                  :disabled="!item.dataBefore && !item.dataAfter"
                >
                  <v-icon small>mdi-eye</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </template>
          <template v-slot:footer.page-text="items"> {{ items.pageStart }} - {{ items.pageStop }} {{$t('of')}} {{ items.itemsLength }} </template>
        </v-data-table>
      </v-card>
      <v-skeleton-loader class="mx-auto" type="table-tbody" v-if="firstLoad && loading"></v-skeleton-loader>
    </v-container>
    <v-dialog v-model="detailDialog" max-width="700px">
      <!-- <v-container fluid class="pa-1"> -->
      <v-card class>
        <v-card-title>
          <span class="headline ml-2">{{$t('data_detail')}}</span>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col>
              <label>{{$t('data_before')}}</label>
              <v-text-field
                v-for="(item,i) in difHeader"
                v-bind:key="i"
                :label="capitalize(item)"
                :value="checkValueOveride(dataBefore, item)"
                readonly
              ></v-text-field>
            </v-col>

            <v-col>
              <label>{{$t('data_after')}}</label>
              <v-text-field
                v-for="(item,i) in difHeader"
                v-bind:key="i"
                :label="capitalize(item)"
                :value="checkValueOveride(dataAfter, item)"
                readonly
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      <!-- </v-container> -->
    </v-dialog>
  </div>
</template>
<script>
import * as moment from "moment";
export default {
  name: "zones",
  data() {
    return {
      firstLoad: true,
      disableSort: true,
      totalData: 0,
      page: 1,
      itemPerPage: 10,
      headers: [
        { text: this.$i18n.t('user'), value: "user.full_name" },
        // { text: "Role", value: "user.role" },
        { text: this.$i18n.t('menu'), value: "menu" },
        { text: this.$i18n.t('action'), value: "userEvent" },
        // { text: "Event", value: "message" },
        // { text: "End Point", value: "endPoint" },
        // { text: "Detail", value: "detail" },
        { text: this.$i18n.t('created_date'), value: "transactionDate" },
        { text: this.$i18n.t('manage'), value: "action" },

      ],
      id: '',
      loading: true,
      editedIndex: -1,
      search: "",
      userList: [],
      items: [],
      menu_start: false,
      menu_end: false,
      start_date: "",
      end_date: "",
      notif: {
        show: false,
        type: '',
        text: []
      },
      actionId: null,
      menuId: 'All',
      actionList: [
        { id: null, name: this.$i18n.t('all') },
        { id: 1, name: this.$i18n.t('insert_add') },
        { id: 2, name: this.$i18n.t('update_edit') },
        { id: 3, name: this.$i18n.t('delete') },
      ],
      detailDialog: false,

      dataBefore: {},
      dataAfter: {},
      todayDate: moment().format('YYYY-MM-DD'),
      difHeader: [],
      menuList: [
        { id: 0, name: this.$i18n.t('all'), key: 'All' },
        { id: 1, name: this.$i18n.t('summary_report_title'), key: 'Summary Report' },
        { id: 2, name: this.$i18n.t('dashboard'), key: 'Dashboard' },
        { id: 3, name: this.$i18n.t('daily_attendance'), key: 'Daily Attendance' },
        { id: 4, name: this.$i18n.t('user_attendance'), key: 'User Attendance' },
        { id: 5, name: this.$i18n.t('activity_report'), key: 'Activity Report' },
        { id: 6, name: this.$i18n.t('monthly_report'), key: 'Monthly Report' },
        { id: 7, name: this.$i18n.t('user_report'), key: 'User Report' },
        { id: 8, name: this.$i18n.t('issue_report'), key: ' Issue Report' },
        { id: 9, name: this.$i18n.t('employee'), key: 'Employee' },
        { id: 10, name: this.$i18n.t('group'), key: 'Groups' },
        { id: 11, name: this.$i18n.t('calendar'), key: 'Calendar' },
        { id: 12, name: this.$i18n.t('zone'), key: 'Zones' },
        { id: 13, name: this.$i18n.t('audit_trail'), key: 'Audit Trail' },
        { id: 14, name: this.$i18n.t('approval'), key: 'Approval' },
        { id: 15, name: this.$i18n.t('edit_company'), key: 'Edit Company' },
        { id: 16, name: this.$i18n.t('list_activity'), key: 'List Activity' },
        { id: 17, name: this.$i18n.t('schedule'), key: 'Schedule' },
        { id: 18, name: this.$i18n.t('master_data'), key: 'Master Data' },
        { id: 19, name: this.$i18n.t('issue_attendance'), key: 'Issue Attendance' },
        { id: 20, name: this.$i18n.t('daily_report'), key: 'Daily Report' },
        { id: 21, name: this.$i18n.t('task_assignment'), key: 'Task Assignment' },
        { id: 22, name: this.$i18n.t('dashboard_attendance'), key: 'Dashboard Attendance' },
        { id: 23, name: this.$i18n.t('admin_user_data'), key: 'Admin User Data' },
        { id: 24, name: this.$i18n.t('admin_company_data'), key: 'Admin Company Data' },
        { id: 25, name: this.$i18n.t('admin_company_config'), key: 'Admin Company Config' },
        { id: 26, name: this.$i18n.t('admin_user_analytics'), key: 'Admin User Analytics' },
        { id: 27, name: this.$i18n.t('admin_manual_cleansing'), key: 'Admin Manual Cleansing' },
        { id: 28, name: this.$i18n.t('admin_dashboard'), key: 'Admin Dashboard' },
        { id: 29, name: this.$i18n.t('admin_checkin_history'), key: 'Admin Check in History' },
        { id: 30, name: this.$i18n.t('admin_payment'), key: 'Admin Payment' },
        { id: 31, name: this.$i18n.t('login'), key: 'Login' },
        { id: 32, name: this.$i18n.t('news_feed'), key: 'News Feed' },
      ]
    };
  },
  computed: {},
  methods: {
    getTimezone(time) {
      switch (parseInt(time)) {
        case 7: return 'WIB'
        case 8: return 'WITA'
        case 9: return 'WIT'

        default: return time
      }
    },
    getRole(role) {
      switch (parseInt(role)) {
        case 0: return this.$i18n.t('admin_hrd')
        case 1: return this.$i18n.t('staff')
        case 2: return this.$i18n.t('admin_appsensi')
        case 3: return this.$i18n.t('supervisor')
        case 4: return this.$i18n.t('executive')

        default: return role
      }
    },
    checkValueOveride(data, item) {
      switch (item) {
        case 'role': return this.getRole(data[item])
        case 'timezone': return this.getTimezone(data[item])
        case 'status':
        case 'strictAreaIn':
        case 'strictAreaOut':
        case 'mandatoryCheckOut':
          return data[item] ? this.$i18n.t('active') : `${this.$i18n.t('not')} ${this.$i18n.t('active')}`

        default:
          return data[item]
      }
    },
    async getDownload() {
      const user_id = this.id ? this.id : '';
      const start_date = this.queryDate(this.start_date);
      const end_date = this.queryDatePlus(this.end_date, 0);
      const companyId = this.$store.state.company;
      const formatStartDate = moment(start_date, 'MM-DD-YYYY').format('DD-MM-YYYY')
      const formatEndDate = moment(end_date, 'MM-DD-YYYY').format('DD-MM-YYYY')
      // const params = {
      //   userId: user_id,
      //   startDate: start_date,
      //   endDate: end_date
      // }
      let url = `?companyId=${companyId}&userId=${user_id}&startDate=${formatStartDate}&endDate=${formatEndDate}`
      if (this.actionId) url = `${url}&actionId=${this.actionId}`
      if (this.menuId != 'All') url = `${url}&menu=${this.menuId}`

      await this.$store.dispatch('getAuditTrailDownload', url)
    },
    capitalize(str = '') {
      // let str2 = str.replace(/([a-z])([A-Z])/g, '$1 $2');
      // return str2.charAt(0).toUpperCase() + str2.slice(1);
      return this.$i18n.t(str)
    },
    capitalizeNoTranslate(str = '') {
      let str2 = str.replace(/([a-z])([A-Z])/g, '$1 $2');
      return str2.charAt(0).toUpperCase() + str2.slice(1);
    },
    getKeyMandatory(data = []) {
      const key = ['fullName', 'name', 'zoneName', 'alias', 'groupName']
      const result = []
      key.map(item => {
        if (data.indexOf(item) >= 0) result.push(item)
      })
      return result
    },
    onlyUnique(value, index, self) {
      return self.indexOf(value) === index;
    },
    getObjectDiff(obj1, obj2) {
      const obj1Props = Object.keys(obj1);
      const obj2Props = Object.keys(obj2);
      const result = [].concat(this.getKeyMandatory(obj1Props), this.getKeyMandatory(obj2Props))

      const keysWithDiffValue = obj1Props.reduce((keysWithDiffValueAccumulator, key) => {
        const propExistsOnObj2 = obj2.hasOwnProperty(key);
        const hasNestedValue = obj1[key] instanceof Object && obj2[key] instanceof Object;
        const keyValuePairBetweenBothObjectsIsEqual = obj1[key] === obj2[key];

        if (!propExistsOnObj2) {
          keysWithDiffValueAccumulator.push(key);
        } else if (hasNestedValue) {
          const keyIndex = keysWithDiffValueAccumulator.indexOf(key);
          if (keyIndex >= 0) {
            keysWithDiffValueAccumulator.splice(keyIndex, 1);
          }
          const nestedDiffs = this.getObjectDiff(obj1[key], obj2[key]);
          for (let diff of nestedDiffs) {
            keysWithDiffValueAccumulator.push(`${key}.${diff}`);
          }
        } else if (keyValuePairBetweenBothObjectsIsEqual) {
          const equalValueKeyIndex = keysWithDiffValueAccumulator.indexOf(key);
          keysWithDiffValueAccumulator.splice(equalValueKeyIndex, 1);
        }
        return keysWithDiffValueAccumulator;
      }, obj2Props);

      return result.concat(keysWithDiffValue).filter(this.onlyUnique)
    },
    openDetail(item) {
      this.dataBefore = {}
      this.dataAfter = {}
      if (item.dataBefore) {
        this.dataBefore = Object.assign({}, item.dataBefore)
      }
      if (item.dataAfter) {
        this.dataAfter = Object.assign({}, item.dataAfter)
      }
      this.difHeader = this.getObjectDiff(this.dataBefore, this.dataAfter)
      this.detailDialog = true
    },
    showNotif(type, text) {
      this.notif.show = false
      this.notif.type = 'primary'
      this.notif.text = this.parsing(text)
    },
    parsing(data) {
      let result = '-'
      if (data) {
        if (Array.isArray(data)) {
          data.map((item) => {
            const keys = Object.keys(item)
            keys.map((key) => {
              if (this.allowedKey(key)) {
                // result.push(`${item[key]} `)
                result = '-'
              }
            })
            // result.push('< ---------------- >')
          })
        } else {
          const keys = Object.keys(data)
          keys.map(key => {
            if (this.allowedKey(key)) {
              // result.push(`${data[key]} `)
              result = `${data[key]}`
            }
          })
        }
      }
      return result
    },
    allowedKey(key) {
      switch (key) {
        case 'full_name':
        case 'alias':
          return true

        default:
          return false
      }
    },
    getAction(action) {
      switch (action) {
        case "add":
          return {
            icon: "mdi-plus-circle-outline",
            color: "primary",
            action: "Create Data "
          };
        case "view":
          return { icon: "mdi-eye", color: "success", action: "View Data" };
        case "edit":
          return {
            // icon: "mdi-tooltip-edit",
            icon: "mdi-pen",
            color: "warning",
            action: "Edit Data"
          };
        case "delete":
          return {
            icon: "mdi-delete",
            color: "error",
            action: "Delete Data"
          };
        case "download":
          return {
            icon: "mdi-download",
            color: "info",
            action: "Download Data"
          };

        default:
          return {
            icon: "mdi-guitar-pick-outline",
            color: "#033B6D",
            action: `${action} Data`
          };
      }
    },
    dateFormatted(date) {
      return moment(date).locale(this.$i18n.t('lang')).format("DD MMMM YYYY");
    },
    dateFormattedHour(date) {
      return moment(date).format("HH:mm:ss");
    },
    queryDate(date) {
      return moment(date).format("MM-DD-YYYY");
    },
    queryDatePlus(date, day) {
      return moment(date)
        .add(day, "days")
        .format("MM-DD-YYYY");
    },
    onSelectUser() {
      this.fetchAudit();
      // setTimeout(() => {
      //   this.id = "";
      // }, 3000);
    },
    onStartDateChange() {
      const diff = moment(this.end_date).diff(this.start_date, 'days')
      if (diff < 0) this.end_date = this.start_date
      this.menu_start = false;
    },
    onEndDateChange() {
      this.menu_end = false;
    },
    checkLimitDate() {
      const start = moment(this.start_date)
      const end = moment(this.end_date)
      const diff = start.diff(end, 'days')
      if (diff < 100 && diff > -100) { return false } else {
        this.showMessage('warning', this.$i18n.t('max_100_days'))
        return true
      }
    },
    showMessage(type, msg) {
      this.$store.dispatch('nShowMessage', { type, msg })
    },
    async fetchAudit(page = 1, itemPerPage = 10) {
      const user_id = this.id ? this.id : '';
      const start_date = this.queryDate(this.start_date);
      const end_date = this.queryDatePlus(this.end_date, 0);
      const formatStartDate = moment(start_date, 'MM-DD-YYYY').format('DD-MM-YYYY')
      const formatEndDate = moment(end_date, 'MM-DD-YYYY').format('DD-MM-YYYY')
      if (this.checkLimitDate()) return;
      this.page = page;
      this.itemPerPage = itemPerPage;
      const totalPageSend = itemPerPage < 1 ? this.totalData : itemPerPage;
      const start = (this.page - 1);
      const params = {
        startDate: formatStartDate,
        endDate: formatEndDate,
        page: start,
        pageSize: totalPageSend,
        userId: user_id,
      }
      if (this.actionId) params.actionId = this.actionId
      if (this.menuId != 'All') params.menu = this.menuId
      this.loading = true;
      const result = await this.$store.dispatch('getAuditTrail', params)
      this.loading = false;
      this.items = result.rows;
      this.totalData = result.count;
      this.firstLoad = false;
    },
    async getUserList() {
      let company_id = this.$store.state.company;
      const result = await this.$store.dispatch('getEmployeeListNoPagination', { companyId: company_id })
      this.userList = result.rows;
      this.userList.unshift({ id: '', full_name: this.$i18n.t('all_user') })
    },

  },
  created() {
    this.start_date = moment().startOf("month").format("YYYY-MM-DD");
    this.end_date = moment().format("YYYY-MM-DD");
    this.fetchAudit();
    this.getUserList();
  }
};
</script>
<style scoped>
.sae-hover {
  cursor: pointer !important;
}
.text-primary {
  color: #033a6d !important;
}
</style>